import {
    Box,
    Typography,
    Paper,
    Chip,
    TextField,
    Stack,
    alpha
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getSingleStat } from '../../../http/statApi.js';
import { getGraphs } from '../../../http/complexesApi.js';
import Viewer from 'react-viewer';

// Иконки
import AssessmentIcon from '@mui/icons-material/Assessment';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WarningIcon from '@mui/icons-material/Warning';
import TrafficIcon from '@mui/icons-material/Traffic';
import StopIcon from '@mui/icons-material/Stop';
import TurnLeftIcon from '@mui/icons-material/TurnLeft';
import WrongLocationIcon from '@mui/icons-material/WrongLocation';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BarChartIcon from '@mui/icons-material/BarChart';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

const ComplexStatistic = props => {
    const { host, hostType = 'default' } = props;
    const isDarkMode = useSelector(state => state.settings.darkMode);
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [statistics, setStatistics] = useState({
        fixations: 0,
        violations: 0,
        violationsWrongDir: 0,
        violationsStopLine: 0,
        violationsRedLight: 0,
        violationsLeftTurn: 0
    });
    const [graph, setGraph] = useState(null);
    const [loading, setLoading] = useState(false);
    const [viewerVisible, setViewerVisible] = useState(false);

    const getStatisticsConfig = () => {
        const baseStats = [
            { key: 'fixations', label: 'Проездов', icon: DirectionsCarIcon, color: isDarkMode ? '#58a6ff' : '#0969da', value: statistics.fixations },
            { key: 'violations', label: 'Нарушений', icon: WarningIcon, color: '#d63a23', value: statistics.violations }
        ];

        if (hostType === 'crossroad') {
            return [
                ...baseStats,
                { key: 'violationsRedLight', label: 'На красный', icon: TrafficIcon, color: '#a23337', value: statistics.violationsRedLight },
                { key: 'violationsStopLine', label: 'Стоп-линия', icon: StopIcon, color: '#f5bb3a', value: statistics.violationsStopLine },
                { key: 'violationsWrongDir', label: 'Встречка', icon: WrongLocationIcon, color: '#28a1d7', value: statistics.violationsWrongDir },
                { key: 'violationsLeftTurn', label: 'Поворот', icon: TurnLeftIcon, color: isDarkMode ? '#bc8cff' : '#8250df', value: statistics.violationsLeftTurn }
            ];
        }

        return baseStats;
    };

    useEffect(() => {
        const loadData = async () => {
            if (!startDate || !endDate) return;

            // Проверяем, что даты в правильном формате и порядке
            if (startDate > endDate) return;

            setLoading(true);

            try {
                // Передаем строки дат напрямую
                const statData = await getSingleStat(startDate, endDate, host.host);
                if (statData) {
                    setStatistics({
                        fixations: statData.totalFixations || 0,
                        violations: statData.totalViolations || 0,
                        violationsWrongDir: statData.totalViolationsWrongDir || 0,
                        violationsStopLine: statData.totalViolationsStopLine || 0,
                        violationsRedLight: statData.totalViolationsRedLight || 0,
                        violationsLeftTurn: statData.totalViolationsLeftTurn || 0
                    });
                }

                // Для графика используем Date объекты с учетом часового пояса +03:00
                const now = new Date()
                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                
                // Начальная дата - всегда 00:00:00 + 3 часа
                const sDate = new Date(startDate)
                sDate.setHours(0, 0, 0, 0)
                sDate.setTime(sDate.getTime() + 3 * 60 * 60 * 1000) // +03:00
                
                // Конечная дата - если сегодня, то текущее время, иначе 23:59:59 + 3 часа
                const eDate = new Date(endDate)
                const endDateOnly = new Date(eDate.getFullYear(), eDate.getMonth(), eDate.getDate())
                
                if (endDateOnly.getTime() === today.getTime()) {
                    // Если конец периода - сегодняшний день, ставим текущее время + 3 часа
                    eDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds())
                    eDate.setTime(eDate.getTime() + 3 * 60 * 60 * 1000) // +03:00
                } else {
                    // Иначе ставим время 23:59:59 + 3 часа
                    eDate.setHours(23, 59, 59, 999)
                    eDate.setTime(eDate.getTime() + 3 * 60 * 60 * 1000) // +03:00
                }

                const graphData = await getGraphs(host.hostid, 'Нарушения', sDate.toISOString().replace('T', ' ').slice(0, -5), eDate.toISOString().replace('T', ' ').slice(0, -5));
                if (graphData && graphData.graphs && graphData.graphs.length > 0) {
                    setGraph(graphData.graphs[0]);
                } else {
                    setGraph(null);
                }
            } catch (error) {
                console.error('Error loading statistics:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [startDate, endDate, host.hostid]);

    const formatNumber = (num) => new Intl.NumberFormat('ru-RU').format(num);

    const getPercentage = (value) => {
        if (statistics.fixations === 0) return 0;
        return ((value / statistics.fixations) * 100).toFixed(1);
    };

    const statsConfig = getStatisticsConfig();

    // Стили для TextField
    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            backgroundColor: isDarkMode ? 'var(--background-subtle)' : 'var(--background-default)',
            borderRadius: { xs: '6px', md: '8px' },
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            '& fieldset': { borderColor: 'var(--border-default)' },
            '&:hover fieldset': { borderColor: 'var(--primary-color)' },
            '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' }
        },
        '& .MuiInputLabel-root': { 
            color: 'var(--text-muted)',
            fontSize: { xs: '0.75rem', md: '0.875rem' }
        },
        '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
        '& .MuiOutlinedInput-input': { 
            color: 'var(--text-primary)',
            fontSize: { xs: '0.75rem', md: '0.875rem' }
        }
    };

    return (
        <Box sx={{ 
            p: { xs: 1, md: 1.5 }, 
            width: '100%', 
            height: '100%', 
            overflow: 'auto',
            backgroundColor: 'var(--background-default)',
            '&::-webkit-scrollbar': { width: { xs: '4px', md: '6px' } },
            '&::-webkit-scrollbar-track': { background: 'var(--background-subtle)', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': { 
                background: 'var(--border-default)', 
                borderRadius: '3px',
                '&:hover': { background: 'var(--text-muted)' }
            }
        }}>
            {/* Панель выбора периода */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center', 
                gap: { xs: 1, md: 1.5 }, 
                mb: { xs: 1, md: 1.5 },
                p: { xs: 1, md: 1.25 },
                backgroundColor: 'var(--background-paper)',
                borderRadius: { xs: '8px', md: '10px' },
                border: '1px solid var(--border-default)',
                flexWrap: { xs: 'wrap', md: 'nowrap' }
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 28, md: 32 },
                    height: { xs: 28, md: 32 },
                    borderRadius: { xs: '6px', md: '8px' },
                    backgroundColor: isDarkMode ? '#58a6ff' : '#0969da',
                    color: 'white',
                    flexShrink: 0
                }}>
                    <CalendarTodayIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1 }, minWidth: 0 }}>
                    <TextField
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        sx={{ 
                            ...textFieldSx, 
                            width: { xs: '100%', md: 150 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: { xs: '6px', md: '8px' },
                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                            }
                        }}
                    />
                    <Typography sx={{ color: 'var(--text-muted)', fontSize: { xs: '0.8rem', md: '0.9rem' }, flexShrink: 0 }}>—</Typography>
                    <TextField
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        sx={{ 
                            ...textFieldSx, 
                            width: { xs: '100%', md: 150 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: { xs: '6px', md: '8px' },
                                fontSize: { xs: '0.75rem', md: '0.875rem' }
                            }
                        }}
                    />
                </Box>

                {statistics.fixations > 0 && (
                    <Chip
                        label={`${formatNumber(statistics.fixations)} проездов`}
                        size="small"
                        sx={{
                            height: { xs: 22, md: 26 },
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            fontWeight: 600,
                            backgroundColor: alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.1),
                            color: isDarkMode ? '#58a6ff' : '#0969da',
                            border: `1px solid ${alpha(isDarkMode ? '#58a6ff' : '#0969da', 0.3)}`,
                            flexShrink: 0,
                            width: { xs: '100%', md: 'auto' }
                        }}
                    />
                )}
            </Box>

            {/* Статистика */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: { xs: 0.75, md: 1 },
                mb: { xs: 1, md: 1.5 },
                '& > *': {
                    flex: { 
                        xs: '1 1 calc(50% - 0.375rem)', 
                        sm: '1 1 calc(33.333% - 0.667rem)', 
                        md: '0 0 auto' 
                    },
                    minWidth: { xs: 'auto', md: '150px' },
                    maxWidth: { xs: 'none', md: '200px' }
                }
            }}>
                {statsConfig.map(config => {
                    const IconComponent = config.icon;
                    const isViolation = config.key !== 'fixations';
                    const percentage = isViolation && config.value > 0 ? getPercentage(config.value) : null;

                    return (
                        <Paper
                            key={config.key}
                            elevation={0}
                            sx={{
                                p: { xs: 1, md: 1.25 },
                                borderRadius: { xs: '8px', md: '10px' },
                                backgroundColor: 'var(--background-paper)',
                                border: `1px solid ${config.value > 0 ? alpha(config.color, 0.3) : 'var(--border-default)'}`,
                                textAlign: 'center',
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                    boxShadow: isDarkMode ? '0 4px 12px rgba(0,0,0,0.3)' : '0 4px 12px rgba(0,0,0,0.1)',
                                    borderColor: config.color
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 0.4, md: 0.5 }, mb: { xs: 0.5, md: 0.75 } }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: { xs: 20, md: 22 },
                                    height: { xs: 20, md: 22 },
                                    borderRadius: { xs: '4px', md: '6px' },
                                    backgroundColor: alpha(config.color, 0.15),
                                    color: config.color
                                }}>
                                    <IconComponent sx={{ fontSize: { xs: 12, md: 14 } }} />
                                </Box>
                                <Typography sx={{ 
                                    fontSize: { xs: '0.65rem', md: '0.7rem' }, 
                                    color: 'var(--text-muted)',
                                    fontWeight: 500
                                }}>
                                    {config.label}
                                </Typography>
                            </Box>

                            <Typography sx={{
                                color: config.value > 0 ? config.color : 'var(--text-muted)',
                                fontWeight: 700,
                                fontSize: { xs: '1rem', md: '1.25rem' },
                                lineHeight: 1.2
                            }}>
                                {formatNumber(config.value)}
                            </Typography>

                            {percentage && (
                                <Typography sx={{ 
                                    fontSize: { xs: '0.6rem', md: '0.65rem' }, 
                                    color: 'var(--text-muted)',
                                    mt: 0.25
                                }}>
                                    {percentage}%
                                </Typography>
                            )}
                        </Paper>
                    );
                })}
            </Box>

            {/* График */}
            {graph && (
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: { xs: '8px', md: '12px' },
                        backgroundColor: 'var(--background-paper)',
                        border: '1px solid var(--border-default)',
                        overflow: 'hidden',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                            boxShadow: isDarkMode ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.1)',
                        }
                    }}
                >
                    <Box sx={{
                        p: { xs: 0.75, md: 1 },
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.75, md: 1 },
                        backgroundColor: 'var(--background-subtle)',
                        borderBottom: '1px solid var(--border-default)'
                    }}>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: { xs: 20, md: 24 },
                            height: { xs: 20, md: 24 },
                            borderRadius: { xs: '4px', md: '6px' },
                            backgroundColor: 'var(--success-color)',
                            color: 'white',
                            flexShrink: 0
                        }}>
                            <BarChartIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
                        </Box>
                        <Typography sx={{ 
                            fontSize: { xs: '0.75rem', md: '0.8rem' }, 
                            fontWeight: 500,
                            color: 'var(--text-primary)',
                            flex: 1
                        }}>
                            График нарушений
                        </Typography>
                    </Box>

                    <Box
                        onClick={() => setViewerVisible(true)}
                        sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            '&:hover .zoom-overlay': { opacity: 1 }
                        }}
                    >
                        <img
                            src={`data:image/jpeg;base64,${graph[1]}`}
                            width="100%"
                            alt="График статистики"
                            style={{ display: 'block' }}
                        />
                        <Box
                            className="zoom-overlay"
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                opacity: 0,
                                transition: 'opacity 0.2s ease',
                            }}
                        >
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: { xs: 0.4, md: 0.5 },
                                px: { xs: 1, md: 1.5 },
                                py: { xs: 0.5, md: 0.75 },
                                borderRadius: { xs: '6px', md: '8px' },
                                backgroundColor: 'rgba(255,255,255,0.9)',
                                color: '#000'
                            }}>
                                <ZoomInIcon sx={{ fontSize: { xs: 16, md: 18 } }} />
                                <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' }, fontWeight: 500 }}>
                                    Увеличить
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Viewer
                        zIndex={9999}
                        visible={viewerVisible}
                        onClose={() => setViewerVisible(false)}
                        images={[{
                            src: `data:image/jpeg;base64,${graph[1]}`,
                            alt: 'График статистики'
                        }]}
                    />
                </Paper>
            )}

            {/* Нет данных */}
            {!loading && statistics.fixations === 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: 'center',
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid var(--border-default)'
                    }}
                >
                    <AssessmentIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--text-muted)', mb: { xs: 1.5, md: 2 }, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                        Нет данных за выбранный период
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                        Измените период или проверьте наличие данных
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default ComplexStatistic;
